import {
  getAPIConfig,
  getAlertsAPIConfig,
  getArchivistAPIConfig,
  getV2APIConfig,
} from '@hooks/useApiQuery';
import { ApiMutationOptions } from '@src/types/ApiQuery';
import { UseMutationOptions, useMutation } from '@tanstack/react-query';

const setFileBody = (file: File) => {
  const formData = new FormData();
  formData.append('file', file);

  return formData;
};

const useApiMutation = <TRequest, TResponse>(
  apiOptions: ApiMutationOptions,
  mutationOptions: Omit<
    UseMutationOptions<TResponse, Error, TRequest>,
    'mutationFn'
  > = {},
) =>
  useMutation<TResponse, Error, TRequest>({
    ...mutationOptions,
    mutationFn: async (request: TRequest): Promise<TResponse> => {
      let apiConfig;
      if (apiOptions.useArchivistAPI) {
        apiConfig = getArchivistAPIConfig(apiOptions.apiPath);
      } else if (apiOptions.useAlertsAPI) {
        apiConfig = getAlertsAPIConfig(apiOptions.apiPath);
      } else if (apiOptions.useV2API) {
        apiConfig = getV2APIConfig(apiOptions.apiPath);
      } else {
        apiConfig = getAPIConfig(apiOptions.apiPath);
      }
      const apiResponse = await fetch(apiConfig.url, {
        method: apiOptions.method,
        headers: Object.assign(
          { authorization: `Bearer ${apiConfig.bearerToken}` },
          !apiOptions.isFileUpload && { 'Content-Type': 'application/json' },
          apiOptions.headers || {},
        ),
        ...(request !== null
          ? {
              body: apiOptions.isFileUpload
                ? setFileBody(request as File)
                : JSON.stringify(request),
            }
          : {}),
      });

      if (!apiResponse.ok) {
        throw new Error(`Failed to fetch from ${apiResponse.url}`, {
          cause: await apiResponse.json(),
        });
      }

      return apiResponse.json();
    },
  });

export default useApiMutation;
